// Klientská část pexesa: drží model a podle režimu zapíná zvukovou a vizuální vrstvu.
import type { GameEvent } from "../../../games/types.ts";
import type { PexesoView } from "../../../games/pexeso/meta.ts";
import { hasAudio, hasVisual, touchReader, usesKeyboard, type Mode } from "../../store.ts";
import type { ClientGame, ClientGameModule, GameContext } from "../types.ts";
import { nextPlayer, resultText, turnText, type Model } from "./model.ts";
import { SoundLayer } from "./sound-layer.ts";
import { VisualLayer } from "./visual-layer.ts";

class PexesoClient implements ClientGame {
  private model: Model;
  private sound: SoundLayer | null = null;
  private visual: VisualLayer | null = null;

  private ctx: GameContext;

  constructor(ctx: GameContext, mode: Mode) {
    this.ctx = ctx;
    this.model = { me: ctx.me, view: null, cursor: 0, sendAction: ctx.sendAction };
    this.setMode(mode);
  }

  setMode(mode: Mode) {
    if (hasAudio(mode) && !this.sound) this.sound = new SoundLayer(this.model, this.ctx.board, () => this.visual?.render());
    if (!hasAudio(mode) && this.sound) {
      this.sound.destroy();
      this.sound = null;
    }
    if (hasVisual(mode) && !this.visual) this.visual = new VisualLayer(this.model, this.ctx.board);
    if (!hasVisual(mode) && this.visual) {
      this.visual.destroy();
      this.visual = null;
    }
    if (this.visual) {
      this.visual.showCursor = usesKeyboard(mode);
      this.visual.setAccessible(touchReader(mode));
    }
  }

  update(view: unknown, events: GameEvent[], initial: boolean) {
    const v = view as PexesoView;
    const first = !this.model.view;
    this.model.view = v;
    if (first || this.model.cursor >= v.cards.length) this.model.cursor = 0;
    this.renderStatus();
    this.visual?.render();
    if (!initial) {
      this.sound?.onEvents(events);
      this.visual?.onEvents(events);
    }
  }

  private renderStatus() {
    const v = this.model.view!;
    const el = this.ctx.status;
    el.textContent = "";
    const turn = document.createElement("span");
    turn.className = "status-turn" + (v.turn === this.model.me && !v.over ? " is-me" : "");
    turn.textContent = v.over ? "Konec hry" : turnText(v, this.model.me).replace(/\.$/, "");
    el.appendChild(turn);
    // Kdo bude na tahu po něm (při neshodě); u více hráčů se v tom snadno ztratí.
    const next = nextPlayer(v);
    if (next && !v.over) {
      const n = document.createElement("span");
      n.className = "status-next";
      n.textContent = `Další: ${next === this.model.me ? "ty" : v.players.find((p) => p.id === next)!.name}`;
      el.appendChild(n);
    }
    v.players.forEach((p, i) => {
      const s = document.createElement("span");
      s.className = `status-score owner-${i}`;
      s.textContent = `${p.name}: ${p.score}`;
      el.appendChild(s);
    });
  }

  rejected() {
    this.sound?.rejected();
  }

  resultText() {
    return this.model.view ? resultText(this.model.view, this.model.me) : "";
  }

  destroy() {
    this.sound?.destroy();
    this.visual?.destroy();
  }
}

export const pexesoClient: ClientGameModule = {
  id: "pexeso",
  boardLabel: "Hrací plocha pexesa. Šipky pohyb, Enter otočit, R stav hry, H nápověda.",
  mount: (ctx, mode) => new PexesoClient(ctx, mode),
};
